import { Link } from "@tanstack/react-router";
import { doc, getDoc } from "firebase/firestore";
import { NotebookPen } from "lucide-react";
import { useEffect, useState } from "react";
import { z } from "zod";

import { db } from "integrations/firebase/firestore";

// The session, not useAuthUser: integrations/firebase/auth runs getAuth() at
// module load, which every component test rendering the directory would pay.
import { useSession } from "../auth/session";
import { PersonNoteEditor } from "./PersonNoteEditor";

// The fields of the author's own membership this section needs: their roster
// row, to hide the note on their own profile, and the event card snapshot
// the membership already carries, to label the note once the event is gone.
const authorMembershipSchema = z.object({
  attendeeId: z.string().optional(),
  eventTitle: z.string().optional(),
  eventSlug: z.string().nullable().optional(),
});
type AuthorMembership = z.infer<typeof authorMembershipSchema>;

type MembershipLookup =
  | { status: "loading" }
  | { status: "absent" }
  | { status: "ready"; membership: AuthorMembership };

/**
 * The private note on another attendee's profile sheet.
 *
 * Self-contained on purpose: it resolves the signed-in user (from the
 * session the app shell provides) and their
 * membership itself, so the sheet and the directory only pass the person, and
 * a change to either leaves this alone. Renders nothing on the author's own
 * profile, and nothing for somebody who is not a member (the rules refuse a
 * first note from them anyway).
 *
 * `fallbackEvent` is the connections page's exception: a note about somebody
 * you met outlives the event and your membership, so the editor stays, with
 * the event named from the connection card.
 */
export function PersonNotesSection({
  eventId,
  attendeeId,
  personName,
  fallbackEvent,
}: {
  eventId: string;
  attendeeId: string;
  personName: string;
  fallbackEvent?: { title: string; slug: string };
}) {
  const uid = useSession()?.uid ?? null;
  const [lookup, setLookup] = useState<MembershipLookup>({
    status: "loading",
  });

  useEffect(() => {
    if (!uid) return;
    let cancelled = false;
    setLookup({ status: "loading" });
    getDoc(doc(db, "events", eventId, "members", uid))
      .then((snapshot) => {
        if (cancelled) return;
        const parsed = authorMembershipSchema.safeParse(snapshot.data() ?? {});
        setLookup(
          snapshot.exists() && parsed.success
            ? { status: "ready", membership: parsed.data }
            : { status: "absent" },
        );
      })
      .catch(() => {
        if (!cancelled) setLookup({ status: "absent" });
      });
    return () => {
      cancelled = true;
    };
  }, [eventId, uid]);

  if (!uid || lookup.status === "loading") return null;
  if (lookup.status === "absent" && !fallbackEvent) return null;
  const membership: AuthorMembership =
    lookup.status === "ready"
      ? lookup.membership
      : { eventTitle: fallbackEvent?.title, eventSlug: fallbackEvent?.slug };
  if (membership.attendeeId === attendeeId) return null;

  return (
    <section
      className="space-y-3 border-t border-border pt-5"
      aria-label="Private note"
      data-testid="person-notes-section"
    >
      <PersonNoteEditor
        // One editor per person, so switching people never carries text over.
        key={`${eventId}/${attendeeId}`}
        target={{
          uid,
          eventId,
          attendeeId,
          personName,
          eventTitle: membership.eventTitle,
          eventSlug: membership.eventSlug ?? undefined,
        }}
      />
      <Link
        to="/notes"
        className="inline-flex min-h-11 items-center gap-2 text-sm font-medium text-lit underline-offset-4 hover:underline"
        data-testid="person-notes-all-link"
      >
        <NotebookPen className="h-4 w-4" />
        All my notes
      </Link>
    </section>
  );
}
